// Adapted from pinpointedu/frontend/apps/product/src/next/app/ProductShell.tsx
// (provider order, boundary-outside-Auth0 rationale carried up into App.tsx;
// dev toolbar and SAT redirects dropped -- §9 of the frontend proposal).
//
// Rewritten 2026-09-06 (UX execution plan §1.5 / WS-0 step 9) to the design's
// per-band chrome: bottom tab bar (<720), 84px rail (720-999), 220px sidebar
// (>=1000) with brand, three tabs, Transactions sub-links, a Hide-balances
// switch and Settings. The header carries a range control, an account
// selector, an Ask entry point (⌘K), and a connection-status pill. The
// sync-health strip is rebuilt on `useItems()` (item-level connection
// health) instead of the impossible `account.status !== 'ok'` filter
// (§9.6 / §4 rule 2 of the plan -- `status` on an account is never `'ok'`).
//
// Publishes `useShellContext()` -- band, range, accountIds (with the
// deprecated accountFilter compatibility accessor), masked, setMasked,
// openAsk() -- as the contract every later workstream builds against
// (execution plan §8).
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type Ref,
  type ReactNode,
} from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  PRIMARY_DESTINATIONS,
  primaryDestinationHref,
  primaryDestinationLabel,
} from '../navigation/destinations';
import { viewportBandForWidth, type ShellViewportBand } from '../navigation/viewportBand';
import { AccountMenu } from './AccountMenu';
import { useItems } from '@/api/hooks/useItems';
import { useAccounts, type Account, type CanonicalAccount } from '@/api/hooks/useAccounts';
import type { ReadScope } from '@/api/hooks/useReadScope';
import { AskOverlay } from '@/features/ask/AskOverlay';
import { useMoneyFormat } from '@/lib/money';
import { parseScope, presetBounds, replaceScope, scopeHref } from './scope';
import {
  Switch,
  ToggleGroup,
  ToggleGroupItem,
} from '@moneyskyline/ui';

export type RangeOption = '1M' | '3M' | 'YTD' | '1Y' | 'All';
const RANGE_OPTIONS: RangeOption[] = ['1M', '3M', 'YTD', '1Y', 'All'];

export interface ShellContextValue {
  band: ShellViewportBand;
  range: RangeOption | 'Custom';
  setRange: (range: RangeOption | 'Custom') => void;
  dateFrom?: string;
  dateTo?: string;
  dateScopeAvailability: 'ready' | 'invalid';
  accountIds?: readonly string[];
  rawAccountFilters?: readonly string[];
  accountScopeAvailability?: 'pending' | 'ready' | 'invalid' | 'unavailable';
  /** @deprecated Compatibility accessor for scalar consumers; use accountIds. */
  accountFilter: string | null;
  accountLabel?: string;
  setAccountIds?: (accountIds: readonly string[]) => void;
  /** @deprecated Compatibility setter for scalar consumers; use setAccountIds. */
  setAccountFilter: (accountId: string | null) => void;
  readScope: 'current' | 'saved_history' | null;
  scopeAvailability: 'pending' | 'ready' | 'error';
  currentAccounts: ReturnType<typeof useAccounts>['data'];
  savedAccounts: ReturnType<typeof useAccounts>['data'];
  canonicalAccounts?: CanonicalAccount[];
  retryScopeAvailability: () => void;
  masked: boolean;
  setMasked: (masked: boolean) => void;
  openAsk: () => void;
}

const ShellContext = createContext<ShellContextValue | null>(null);

/** The contract every later workstream builds against (execution plan §8). */
export function useShellContext(): ShellContextValue {
  const ctx = useContext(ShellContext);
  if (!ctx) throw new Error('useShellContext must be used within AppShell');
  return ctx;
}

function useViewportBand(): ShellViewportBand {
  const [band, setBand] = useState<ShellViewportBand>(() =>
    typeof window === 'undefined' ? 'desktop' : viewportBandForWidth(window.innerWidth),
  );

  useEffect(() => {
    const onResize = () => setBand(viewportBandForWidth(window.innerWidth));
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return band;
}

const TRANSACTIONS_SUB_LINKS = [
  { href: '/transactions/inbox', label: 'Inbox' },
  { href: '/transactions/categories', label: 'By category' },
  { href: '/transactions/merchants', label: 'By merchant' },
];

const BUSINESS_SUB_LINKS = [
  { href: '/business/conditions', label: 'Conditions' },
  { href: '/business/export', label: 'Export review' },
];

/** Renders nothing when there are no connected items -- an honest absence, not a zero state. */
function SyncHealthStrip({ band }: { band: ShellViewportBand }) {
  const { data: items } = useItems();
  if (!items || items.length === 0) return null;

  const degraded = items.filter((item) => item.status !== 'active' || item.bootstrap_status !== 'complete' || !item.last_synced_at);
  const pill = band === 'mobile';

  if (degraded.length === 0) {
    return (
      <div
        className={
          pill
            ? 'border-b bg-muted/40 px-4 py-1.5 text-xs text-muted-foreground'
            : 'rounded-full border border-border bg-muted/40 px-3 py-1 text-xs text-muted-foreground'
        }
        data-testid="sync-health-strip"
      >
        Synced
      </div>
    );
  }

  return (
    <div
      className={
        pill
          ? 'border-b border-degraded-border bg-degraded-surface px-4 py-1.5 text-xs text-degraded-foreground'
          : 'rounded-full border border-degraded-border bg-degraded-surface px-3 py-1 text-xs text-degraded-foreground'
      }
      data-testid="sync-health-strip-degraded"
    >
      {degraded.length} connection{degraded.length === 1 ? '' : 's'} need attention
    </div>
  );
}

function RangePicker({ range, setRange }: { range: RangeOption | 'Custom'; setRange: (r: RangeOption | 'Custom') => void }) {
  return (
    <ToggleGroup
      type="single"
      value={range}
      onValueChange={(value) => value && setRange(value as RangeOption | 'Custom')}
      aria-label="Date range"
    >
      {[...RANGE_OPTIONS, 'Custom' as const].map((option) => (
        <ToggleGroupItem
          key={option}
          value={option}
          size="sm"
          aria-label={option}
          className="group h-11 min-w-11 p-0 hover:bg-transparent data-[state=on]:bg-transparent data-[state=on]:shadow-none"
        >
          <span className="pointer-events-none flex h-8 items-center justify-center rounded-sm px-2 group-data-[state=on]:bg-background group-data-[state=on]:shadow-sm group-data-[state=on]:text-foreground">
            {option}
          </span>
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

function MobileRangePicker({ range, setRange }: { range: RangeOption | 'Custom'; setRange: (r: RangeOption | 'Custom') => void }) {
  return (
    <label className="flex items-center gap-1 text-xs text-muted-foreground max-[719px]:min-w-0 max-[719px]:max-w-full max-[719px]:flex-[1_1_10rem] max-[719px]:flex-wrap">
      Date range
      <select className="min-h-11 rounded-md border border-input bg-background px-1.5 text-xs text-foreground max-[719px]:w-0 max-[719px]:min-w-0 max-[719px]:max-w-full max-[719px]:flex-1" aria-label="Date range" value={range} onChange={(event) => setRange(event.target.value as RangeOption | 'Custom')}>
        {[...RANGE_OPTIONS, 'Custom' as const].map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    </label>
  );
}

type PickerAccount = { pickerId: string; display_name: string; last4: string | null };

type AccountScopeAvailability = 'pending' | 'ready' | 'invalid' | 'unavailable';

function AccountScopePicker({ accountIds, setAccountIds, accounts, availability }: { accountIds: readonly string[]; setAccountIds: (accountIds: readonly string[]) => void; accounts: PickerAccount[] | undefined; availability: AccountScopeAvailability }) {
  const [open, setOpen] = useState(false);
  const listRef = useRef<HTMLFieldSetElement | null>(null);
  const ready = availability === 'ready' && Boolean(accounts);
  const selectedLabels = accountIds
    .map((accountId) => accounts?.find((account) => account.pickerId === accountId)?.display_name)
    .filter((label): label is string => Boolean(label));
  const summary = ready
    ? accountIds.length === 0
      ? 'All accounts'
      : accountIds.length === 1 && selectedLabels[0]
        ? selectedLabels[0]
        : `${accountIds.length} account${accountIds.length === 1 ? '' : 's'}`
    : availability === 'pending'
      ? 'Loading accounts…'
      : availability === 'invalid'
        ? 'Invalid account selection'
        : 'Accounts unavailable';
  const statusMessage = availability === 'invalid'
    ? 'The account selection is invalid. Review it or reset to All accounts.'
    : availability === 'pending'
      ? 'Account selection is loading; your URL selection is preserved.'
      : availability === 'unavailable'
        ? 'The selected account is unavailable. Review it or reset to All accounts.'
        : null;
  const showRecoveryActions = availability === 'invalid' || availability === 'unavailable' || (availability === 'pending' && accountIds.length > 0);
  const recoveryButtons = <>
    <button type="button" className="min-h-11 rounded-md px-2 text-xs text-foreground underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => setOpen(true)}>Edit selection</button>
    <button type="button" className="min-h-11 rounded-md px-2 text-xs text-foreground underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => { setAccountIds([]); setOpen(false); }}>Reset to All</button>
  </>;

  useEffect(() => {
    if (open) listRef.current?.focus();
  }, [open]);

  const toggleAccount = (accountId: string, checked: boolean) => {
    if (checked) setAccountIds([...accountIds, accountId]);
    else setAccountIds(accountIds.filter((selectedId) => selectedId !== accountId));
  };

  return (
    <div className="relative flex items-center gap-1 text-xs text-muted-foreground max-[719px]:min-w-0 max-[719px]:max-w-full max-[719px]:flex-[1_1_10rem] max-[719px]:flex-wrap">
      Account
      <button
        type="button"
        className="min-h-11 max-w-48 rounded-md border border-input bg-background px-3 text-left text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 max-[719px]:min-w-0 max-[719px]:max-w-full max-[719px]:flex-1"
        aria-label="Account scope"
        aria-expanded={open}
        aria-controls="account-scope-options"
        onClick={() => setOpen((current) => !current)}
      >
        {summary}
      </button>
      {statusMessage && <div role="status" aria-label="Account scope status" aria-live="polite" className="sr-only">{statusMessage}</div>}
      {showRecoveryActions && !open && (
        <div className="flex flex-wrap gap-1">
          {recoveryButtons}
        </div>
      )}
      {open && (
        <div className="absolute left-0 top-12 z-20 min-w-64 rounded-md border bg-card p-3 shadow-overlay max-[719px]:left-0 max-[719px]:right-0">
          {statusMessage && <div className="mb-2 text-xs text-degraded-foreground">{statusMessage}</div>}
          <fieldset
            ref={listRef}
            id="account-scope-options"
            tabIndex={-1}
            aria-label="Account selection"
            className="flex flex-col gap-1 focus-visible:outline-none"
          >
            <legend className="sr-only">Account selection</legend>
            {ready && (accounts ?? []).map((account) => {
              const selected = accountIds.includes(account.pickerId);
              const limitReached = accountIds.length >= 50 && !selected;
              return (
                <label key={account.pickerId} className="flex min-h-11 items-center gap-2 rounded px-2 text-sm text-foreground focus-within:ring-2 focus-within:ring-ring">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={selected}
                    disabled={limitReached}
                    aria-label={account.display_name}
                    onChange={(event) => toggleAccount(account.pickerId, event.target.checked)}
                  />
                  <span>{account.display_name}</span>
                </label>
              );
            })}
          </fieldset>
          {ready && accountIds.length >= 50 && <div role="status" aria-label="Account selection limit" aria-live="polite" className="mt-2 text-xs text-muted-foreground">50 accounts selected; remove one before selecting another.</div>}
          <div className="mt-2 flex flex-wrap gap-2 border-t pt-2">
            {recoveryButtons}
          </div>
        </div>
      )}
    </div>
  );
}

function HideBalancesToggle({ masked, setMasked }: { masked: boolean; setMasked: (m: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-sm text-muted-foreground">
      <span>Hide balances</span>
      <Switch checked={masked} onCheckedChange={setMasked} aria-label="Hide balances" />
    </label>
  );
}

function MaskToggleButton({ masked, setMasked }: { masked: boolean; setMasked: (m: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => setMasked(!masked)}
      aria-pressed={masked}
      aria-label="Hide balances"
      className="flex h-9 min-h-11 w-11 items-center justify-center rounded-md border text-sm text-muted-foreground"
    >
      ••
    </button>
  );
}

function CustomDateControls({ scope }: { scope: ReturnType<typeof parseScope> }) {
  const navigate = useNavigate();
  const location = useLocation();
  const groupRef = useRef<HTMLDivElement | null>(null);
  const fromRef = useRef<HTMLInputElement | null>(null);
  const defaults = presetBounds('1M', new Date());
  const invalid = scope.dateScopeAvailability === 'invalid';
  const [editing, setEditing] = useState(!invalid);
  const [from, setFrom] = useState(invalid ? scope.rawDateFrom[0] ?? '' : scope.dateFrom ?? defaults.from ?? '');
  const [to, setTo] = useState(invalid ? scope.rawDateTo[0] ?? '' : scope.dateTo ?? defaults.to ?? '');
  const [validationError, setValidationError] = useState<string | null>(null);
  useEffect(() => {
    if (editing) fromRef.current?.focus();
  }, [editing]);
  const resetToAll = () => {
    setValidationError(null);
    navigate({ search: replaceScope(location.search, { ...scope, range: 'All', dateFrom: undefined, dateTo: undefined }), hash: location.hash });
  };
  const apply = () => {
    const draftParams = new URLSearchParams();
    draftParams.set('from', from);
    draftParams.set('to', to);
    const parsed = parseScope(`?${draftParams.toString()}`, new Date());
    if (parsed.dateScopeAvailability === 'ready' && parsed.dateFrom && parsed.dateTo) {
      navigate({ search: replaceScope(location.search, { ...scope, range: 'Custom', dateFrom: parsed.dateFrom, dateTo: parsed.dateTo }), hash: location.hash });
      return;
    }
    setValidationError('Enter a valid start and end date.');
  };
  const onBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (event.relatedTarget && event.currentTarget.contains(event.relatedTarget as Node)) return;
    const draftParams = new URLSearchParams();
    draftParams.set('from', from);
    draftParams.set('to', to);
    const parsed = parseScope(`?${draftParams.toString()}`, new Date());
    if (parsed.dateScopeAvailability === 'invalid') setValidationError('Enter a valid start and end date.');
  };
  if (invalid && !editing) {
    return (
      <div className="flex min-w-0 flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid="date-scope-recovery">
        <span role="status">Invalid date range</span>
        <span data-testid="raw-date-label">Raw URL dates: from={scope.rawDateFrom.join('; ')}; to={scope.rawDateTo.join('; ')}</span>
        <button type="button" className="min-h-11 rounded-md border border-input px-2" onClick={() => setEditing(true)}>Edit dates</button>
        <button type="button" className="min-h-11 rounded-md border border-input px-2" onClick={resetToAll}>Reset to All</button>
      </div>
    );
  }
  return (
    <div ref={groupRef} onBlur={onBlur} className="grid min-w-0 grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-end gap-1 text-xs text-muted-foreground">
      {invalid && <span className="col-span-full">Raw URL dates: from={scope.rawDateFrom.join('; ')}; to={scope.rawDateTo.join('; ')}</span>}
      <label className="min-w-0">From <input ref={fromRef} type={invalid ? 'text' : 'date'} inputMode={invalid ? 'numeric' : undefined} aria-label="From date" value={from} onChange={(event) => setFrom(event.target.value)} className="min-h-11 w-full min-w-0 rounded-md border px-1" /></label>
      <label className="min-w-0">To <input type={invalid ? 'text' : 'date'} inputMode={invalid ? 'numeric' : undefined} aria-label="To date" value={to} onChange={(event) => setTo(event.target.value)} className="min-h-11 w-full min-w-0 rounded-md border px-1" /></label>
      <button type="button" onClick={apply} className="min-h-11 whitespace-nowrap rounded-md border border-input px-2">Apply dates</button>
      {invalid && <button type="button" onClick={resetToAll} className="min-h-11 whitespace-nowrap rounded-md border border-input px-2">Reset to All</button>}
      {validationError && <p role="alert" className="col-span-full text-degraded-foreground">{validationError}</p>}
    </div>
  );
}

function AskBar({ band, onOpen }: { band: ShellViewportBand; onOpen: () => void }) {
  if (band === 'mobile') {
    return (
      <button
        type="button"
        onClick={onOpen}
        aria-haspopup="dialog"
        className="flex min-h-11 w-full items-center justify-center gap-1 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-float"
      >
        › Ask
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-haspopup="dialog"
      className="group flex h-11 min-w-[220px] flex-1 items-center p-0"
    >
      <span className="pointer-events-none flex h-9 w-full items-center justify-between rounded-md border bg-background px-3 text-sm text-muted-foreground">
        <span>Ask MoneySkyline…</span>
        {band === 'desktop' && (
          <kbd className="rounded border border-input px-1.5 py-0.5 text-[10px] text-muted-foreground">⌘K</kbd>
        )}
      </span>
    </button>
  );
}

function PrimaryNavLinks({
  className,
  linkClassName,
  showLabel = true,
  testId,
  navRef,
}: {
  className: string;
  linkClassName: string;
  showLabel?: boolean;
  testId?: string;
  navRef?: Ref<HTMLElement>;
}) {
  const location = useLocation();
  return (
    <nav ref={navRef} aria-label="Primary" className={className} data-testid={testId}>
      {PRIMARY_DESTINATIONS.map((destination) => (
        <NavLink
          key={destination}
          to={scopeHref(primaryDestinationHref(destination), location.search)}
          className={({ isActive }) =>
            [linkClassName, isActive ? 'text-primary font-semibold' : 'text-muted-foreground'].join(' ')
          }
        >
          {showLabel ? primaryDestinationLabel(destination) : primaryDestinationLabel(destination)}
        </NavLink>
      ))}
    </nav>
  );
}

function DesktopSidebar({ masked, setMasked }: { masked: boolean; setMasked: (m: boolean) => void }) {
  const location = useLocation();
  const onTransactions = location.pathname.startsWith('/transactions');
  const onBusiness = location.pathname.startsWith('/business') || location.pathname.startsWith('/events');
  return (
    <aside className="flex w-[220px] shrink-0 flex-col gap-4 border-r bg-sidebar p-4">
      <span className="text-sm font-bold">MoneySkyline</span>
      <PrimaryNavLinks
        className="flex flex-col gap-1"
        linkClassName="rounded-md px-3 py-2 text-sm min-h-11 flex items-center"
      />
      {onTransactions && (
        <nav aria-label="Transactions" className="ml-3 flex flex-col gap-1 border-l pl-3">
          {TRANSACTIONS_SUB_LINKS.map((link) => (
            <NavLink
              key={link.href}
              to={scopeHref(link.href, location.search)}
              className={({ isActive }) =>
                ['text-xs', isActive ? 'text-primary font-semibold' : 'text-muted-foreground'].join(' ')
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>
      )}
      {onBusiness && (
        <nav aria-label="Business" className="ml-3 flex flex-col gap-1 border-l pl-3">
          {BUSINESS_SUB_LINKS.map((link) => (
            <NavLink
              key={link.href}
              to={scopeHref(link.href, location.search)}
              className={({ isActive }) =>
                ['text-xs', isActive ? 'text-primary font-semibold' : 'text-muted-foreground'].join(' ')
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>
      )}
      <div className="mt-auto flex flex-col gap-3">
        <HideBalancesToggle masked={masked} setMasked={setMasked} />
        <NavLink to={scopeHref('/settings', location.search)} className="text-sm text-muted-foreground">
          Settings
        </NavLink>
      </div>
    </aside>
  );
}

function TabletOverflowMenu({ masked, setMasked }: { masked: boolean; setMasked: (m: boolean) => void }) {
  const location = useLocation();
  return (
    <details className="relative shrink-0">
      <summary role="button" aria-label="More options" className="ms-tap-44 flex cursor-pointer list-none items-center rounded-md border px-3 text-sm">
        More
      </summary>
      <div className="absolute right-0 top-11 z-20 flex min-w-44 flex-col gap-3 rounded-md border bg-card p-3 shadow-overlay">
        <HideBalancesToggle masked={masked} setMasked={setMasked} />
        <NavLink to={scopeHref('/settings', location.search)} className="text-sm text-muted-foreground">Settings</NavLink>
      </div>
    </details>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const band = useViewportBand();
  const location = useLocation();
  const navigate = useNavigate();
  const scope = parseScope(location.search);
  const explicitReadScope = scope.readScope;
  const selectedScope: ReadScope | undefined = explicitReadScope ?? undefined;
  const accountsQuery = useAccounts(selectedScope);
  const [pendingCustom, setPendingCustom] = useState<{ key: string; pathname: string } | null>(null);
  const customPending = location.state?.customOpen === true || pendingCustom?.key === location.key;
  const [askOpen, setAskOpen] = useState(false);
  const mobileNavRef = useRef<HTMLElement | null>(null);
  const shellMainRef = useRef<HTMLElement | null>(null);
  const [mobileNavHeight, setMobileNavHeight] = useState(83);
  const { masked, setMasked } = useMoneyFormat();

  const availability = accountsQuery.isPending ? 'pending' : accountsQuery.isError ? 'error' : 'ready';
  const readScope = explicitReadScope;
  const selectedAccounts = accountsQuery.data;
  const pickerAccounts: PickerAccount[] | undefined = selectedAccounts?.flatMap((account: Account | CanonicalAccount) => {
    if (explicitReadScope) return [{ pickerId: account.account_id, display_name: account.display_name, last4: account.last4 }];
    const canonical = account as CanonicalAccount;
    if (canonical.canonical_account_id) return [{ pickerId: canonical.canonical_account_id, display_name: canonical.display_name, last4: canonical.last4 }];
    if (canonical.reconciliation_initialization_required) return [{ pickerId: canonical.account_id, display_name: canonical.display_name, last4: canonical.last4 }];
    return [];
  });
  const retryScopeAvailability = () => {
    void accountsQuery.refetch();
  };

  const accountScopeAvailability: AccountScopeAvailability = scope.accountScopeAvailability === 'invalid'
    ? 'invalid'
    : availability === 'pending'
      ? 'pending'
      : availability === 'error'
        ? 'unavailable'
        : scope.accountIds.some((accountId) => !pickerAccounts?.some((account) => account.pickerId === accountId))
          ? 'unavailable'
          : 'ready';

  const openAsk = () => setAskOpen(true);

  const setRange = (range: RangeOption | 'Custom') => {
    if (range === 'Custom') {
      setPendingCustom({ key: location.key, pathname: location.pathname });
      return;
    }
    setPendingCustom(null);
    if (range === 'All') navigate({ search: replaceScope(location.search, { ...scope, range, dateFrom: undefined, dateTo: undefined }), hash: location.hash });
    else {
      const bounds = presetBounds(range, new Date());
      navigate({ search: replaceScope(location.search, { ...scope, range, dateFrom: bounds.from, dateTo: bounds.to }), hash: location.hash });
    }
  };
  const setAccountIds = (accountIds: readonly string[]) => navigate({
    search: replaceScope(location.search, {
      ...scope,
      accountIds,
      accountFilter: accountIds[0] ?? null,
      rawAccountFilters: [],
      accountScopeAvailability: 'ready',
    }),
    hash: location.hash,
  });
  const setAccountFilter = (accountFilter: string | null) => setAccountIds(accountFilter ? [accountFilter] : []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openAsk();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (band !== 'mobile') return;
    const nav = mobileNavRef.current;
    if (!nav) return;

    const updateHeight = () => {
      const measuredHeight = Math.ceil(nav.getBoundingClientRect().height || nav.offsetHeight);
      if (measuredHeight > 0) setMobileNavHeight(measuredHeight);
    };
    updateHeight();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(nav);
    return () => observer.disconnect();
  }, [band]);

  useEffect(() => {
    // Reserve bottom scroll padding equal to the mobile dock height so the
    // last row remains reachable. §14 decision 3 removed the bottom-right
    // apply-to-merchant tray, so this only needs to clear the mobile bottom
    // nav dock now -- no more `--category-tray-clearance` term.
    if (band !== 'mobile') return;
    const root = document.documentElement;
    const main = shellMainRef.current;
    const previousRootPadding = root.style.scrollPaddingBottom;
    const previousMainPadding = main?.style.scrollPaddingBottom ?? '';
    const clearance = `${mobileNavHeight + 16}px`;

    root.style.scrollPaddingBottom = clearance;
    if (main) main.style.scrollPaddingBottom = clearance;

    return () => {
      root.style.scrollPaddingBottom = previousRootPadding;
      if (main) main.style.scrollPaddingBottom = previousMainPadding;
    };
  }, [band, mobileNavHeight]);

  const shellValue: ShellContextValue = {
      band,
      range: scope.range,
      setRange,
      dateFrom: scope.dateFrom,
      dateTo: scope.dateTo,
      dateScopeAvailability: scope.dateScopeAvailability,
      accountIds: scope.accountIds,
      rawAccountFilters: scope.rawAccountFilters,
      accountScopeAvailability,
      accountFilter: scope.accountFilter,
      accountLabel: scope.accountIds.length === 0
        ? 'All accounts'
        : scope.accountIds.length === 1
          ? pickerAccounts?.find((account) => account.pickerId === scope.accountIds[0])?.display_name
          : `${scope.accountIds.length} accounts`,
      setAccountIds,
      setAccountFilter,
      readScope,
      scopeAvailability: availability,
      currentAccounts: explicitReadScope === 'current' ? accountsQuery.data : undefined,
      savedAccounts: explicitReadScope === 'saved_history' ? accountsQuery.data : undefined,
      canonicalAccounts: explicitReadScope === null && !accountsQuery.isError ? accountsQuery.data as CanonicalAccount[] | undefined : undefined,
      retryScopeAvailability,
      masked,
      setMasked,
      openAsk,
  };

  return (
    <ShellContext.Provider value={shellValue}>
      <div className="flex min-h-screen flex-col bg-background" data-viewport-band={band}>
        <header className={`flex items-center gap-3 border-b bg-background px-4 py-2 ${band === 'mobile' ? 'flex-wrap' : 'flex-wrap'}`}>
          {band === 'mobile' ? (
            <>
              <div className="flex w-full flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-bold">MoneySkyline</span>
                <div className="flex shrink-0 items-center gap-2">
                  <MaskToggleButton masked={masked} setMasked={setMasked} />
                  {/* DES-R2-001: AccountMenu already exposes Settings --
                      mobile must match desktop/tablet (one entry point),
                      not a duplicate standalone link next to the menu. */}
                  <AccountMenu />
                </div>
              </div>
              <div className="flex w-full min-w-0 flex-wrap items-center gap-2">
                <MobileRangePicker range={customPending ? 'Custom' : scope.range} setRange={setRange} />
                <AccountScopePicker accountIds={scope.accountIds} setAccountIds={setAccountIds} accounts={pickerAccounts} availability={accountScopeAvailability} />
                {(scope.range === 'Custom' || customPending) && <CustomDateControls key={`${location.key}${location.search}`} scope={scope} />}
              </div>
            </>
          ) : (
            <>
              <span className="text-sm font-bold md:hidden">MoneySkyline</span>
              <RangePicker range={customPending ? 'Custom' : scope.range} setRange={setRange} />
              <AccountScopePicker accountIds={scope.accountIds} setAccountIds={setAccountIds} accounts={pickerAccounts} availability={accountScopeAvailability} />
              {(scope.range === 'Custom' || customPending) && <CustomDateControls key={`${location.key}${location.search}`} scope={scope} />}
              <AskBar band={band} onOpen={openAsk} />
              <AccountMenu showLabel={band === 'desktop'} />
              <SyncHealthStrip band={band} />
              {band === 'tablet' && <TabletOverflowMenu masked={masked} setMasked={setMasked} />}
            </>
          )}
          {band === 'mobile' && <AskBar band={band} onOpen={openAsk} />}
        </header>
        {band === 'mobile' && <SyncHealthStrip band={band} />}
        <div className="flex flex-1">
          {band === 'desktop' && <DesktopSidebar masked={masked} setMasked={setMasked} />}
          {band === 'tablet' && (
            <PrimaryNavLinks
              className="flex w-[84px] shrink-0 flex-col items-center gap-4 border-r bg-sidebar py-4"
              linkClassName="flex min-h-11 flex-col items-center justify-center text-xs"
            />
          )}
          <main
            ref={shellMainRef}
            data-testid="shell-main"
            data-mobile-nav-clearance={band === 'mobile' ? mobileNavHeight : undefined}
            style={
              band === 'mobile'
                ? { paddingBottom: `calc(${mobileNavHeight}px + env(safe-area-inset-bottom) + 16px)` }
                : undefined
            }
            className="flex-1 overflow-y-auto"
          >
            {location.state?.scopeNotice && <div role="status" aria-live="polite" className="border-b bg-muted/50 px-4 py-2 text-sm">{location.state.scopeNotice}</div>}
            {children}
          </main>
        </div>
        {band === 'mobile' && (
          <PrimaryNavLinks
            className="fixed inset-x-0 bottom-0 z-40 flex min-h-[83px] items-center justify-around border-t bg-background pb-[env(safe-area-inset-bottom)] pt-2"
            linkClassName="flex min-h-11 flex-col items-center justify-center text-xs"
            testId="mobile-primary-nav"
            navRef={mobileNavRef}
          />
        )}
      </div>
      <AskOverlay band={band} open={askOpen} onOpenChange={setAskOpen} />
    </ShellContext.Provider>
  );
}
